import {
  estimateThresholdLabelWidth,
  getThresholdPillCenterY,
  getThresholdPillLayout,
  getThresholdPillTop,
  THRESHOLD_PILL,
} from "../layout";
import {
  getThresholdColor,
  getThresholdSizes,
  ThresholdGeometryProps,
} from "./shared";
import { useTheme } from "@mui/material";
import { useTranslation } from "react-i18next";
import React, {
  FunctionComponent as FC,
  useLayoutEffect,
  useRef,
  useState,
} from "react";

/** Read-only mm labels above each threshold (view mode). */
export const ThresholdPills: FC<Omit<ThresholdGeometryProps, "plotHeight">> = ({
  bands,
  chartPadding: { top: plotTop },
  displaySizes,
  xScale,
}) => {
  const { t } = useTranslation();
  const sizes = getThresholdSizes(bands, displaySizes);

  return sizes.map((threshold, index) => {
    const color = getThresholdColor(bands, index);
    const x = xScale(threshold);
    return (
      <ThresholdPill
        key={threshold}
        label={t("utils.units.valueWithUnit", {
          value: threshold,
          unit: t("utils.units.mm"),
        })}
        plotTop={plotTop}
        sectionColor={color}
        x={x}
      />
    );
  });
};

interface ThresholdPillProps {
  label: string;
  plotTop: number;
  sectionColor: string;
  x: number;
}

const ThresholdPill: FC<ThresholdPillProps> = ({
  label,
  plotTop,
  sectionColor,
  x,
}) => {
  const {
    palette: {
      text: { primary: textPrimary },
    },
  } = useTheme();
  const textRef = useRef<SVGTextElement>(null);
  const [textWidth, setTextWidth] = useState(
    () => estimateThresholdLabelWidth(label) // initial is estimated
  );

  useLayoutEffect(() => {
    const textElement = textRef.current;
    if (!textElement) {
      return;
    }
    const measuredWidth = textElement.getComputedTextLength();
    if (measuredWidth > 0) {
      setTextWidth(measuredWidth);
    }
  }, [label]);

  const pill = getThresholdPillLayout(textWidth);
  const pillTop = getThresholdPillTop(plotTop, pill);
  const pillCenterY = getThresholdPillCenterY(plotTop, pill);

  return (
    <>
      <rect
        x={x - pill.halfWidth}
        y={pillTop}
        width={pill.halfWidth * 2}
        height={pill.height}
        rx={pill.borderRadius}
        fill="none"
        stroke={sectionColor}
        strokeWidth={1}
      />
      <text
        ref={textRef}
        x={x}
        y={pillCenterY}
        textAnchor="middle"
        dominantBaseline="middle"
        fontSize={THRESHOLD_PILL.fontSize}
        fill={textPrimary}
      >
        {label}
      </text>
    </>
  );
};
